import React from 'react';
import { CalendarCheck, CalendarX, DollarSign, FileBadge, Hash, Plus, ShieldCheck } from 'lucide-react';
import { Card, Field, FieldGrid } from './Card';
import ActionButton from './ActionButton';

const STATUS_TONES = {
    ACTIVE: 'tw-bg-green-100 tw-text-green-800',
    CANCELED: 'tw-bg-red-100 tw-text-red-700',
    BIND_FAILED: 'tw-bg-red-100 tw-text-red-700',
    PENDING: 'tw-bg-amber-100 tw-text-amber-800',
    REQUIRES_PAYMENT: 'tw-bg-amber-100 tw-text-amber-800',
};

// The insurer's latest response for a policy: `verticalInsuranceResponse` is an array of updates on
// newer records and a single object on older ones.
const latestResponse = (responses, policyNumber) => {
    const vir = responses?.find(r => r.id === policyNumber)?.verticalInsuranceResponse;
    return Array.isArray(vir) ? vir[vir.length - 1] : vir;
};

const dateOnly = value => (typeof value === 'string' ? value.split('T')[0] : value);

/**
 * Purchased insurance (canceled policies, insuranceStatus 2, are left out as before) and the Add
 * Insurance button.
 *
 * `addState` comes from CamperDetail, which keeps the old rules:
 * - 'add'    → insurance is offered, under MAX_INSURANCE_PRODUCTS, and the program hasn't started;
 * - 'closed' → same, but the program has started (shows the note instead of the button);
 * - null     → nothing (not offered, or the maximum is already purchased).
 */
const InsuranceCard = ({ insuranceData = [], insuranceResponse, addState, onAdd }) => {
    const policies = insuranceData.filter(ins => ins.insuranceStatus !== 2);

    return (
        <Card
            id="insurance"
            title="Insurance"
            icon={ShieldCheck}
            actions={addState === 'add' ? <ActionButton variant="primary" icon={Plus} onClick={onAdd}>Add Insurance</ActionButton> : null}
        >
            {policies.length
                ? <ul className="tw-flex tw-flex-col tw-gap-4">
                    {policies.map((ins, i) => {
                        const response = latestResponse(insuranceResponse, ins?.policyNumber);
                        const status = response?.policy_status;
                        return (
                            <li key={ins?.policyNumber || i} className="tw-border-t tw-border-neutral-100 tw-pt-4 first:tw-border-t-0 first:tw-pt-0">
                                <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                    <span className="tw-text-sm tw-font-semibold tw-text-neutral-800">{i + 1}. {ins?.product?.friendly_name}</span>
                                    {status
                                        ? <span className={`tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold ${STATUS_TONES[status] || 'tw-bg-neutral-100 tw-text-neutral-700'}`}>{status}</span>
                                        : null}
                                </div>
                                <FieldGrid className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 lg:tw-grid-cols-3">
                                    <Field icon={Hash} label="Payment confirmation ID">{ins?.policyNumber}</Field>
                                    <Field icon={FileBadge} label="Status">Purchased</Field>
                                    <Field icon={DollarSign} label="Premium amount">{ins?.total !== undefined && ins?.total !== null ? `$${ins.total}` : null}</Field>
                                    {response?.policyNumber ? <Field icon={Hash} label="Policy number">{response.policyNumber}</Field> : null}
                                    <Field icon={CalendarCheck} label="Effective date">{dateOnly(ins?.effective_date)}</Field>
                                    <Field icon={CalendarX} label="Expiration date">{dateOnly(ins?.expiration_date)}</Field>
                                </FieldGrid>
                            </li>
                        );
                    })}
                </ul>
                : <p className="tw-text-sm tw-italic tw-text-neutral-400">No insurance purchased for this booking.</p>}

            {addState === 'closed'
                ? <p className="tw-mt-3 tw-text-sm tw-text-red-600">Note: Insurance can not be purchased after the program start date.</p>
                : null}
        </Card>
    );
};

export default InsuranceCard;
